import { PageHeader } from '../../components/layout/Header';
import { Button } from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { AppIcon } from '../../components/icons/AppIcon';
import { DEFAULT_COMMISSION_RATE } from '../../domain/partners/types';
import { usePartnerApply } from './hooks/usePartnerApply';

const COMMISSION_PCT = Math.round(DEFAULT_COMMISSION_RATE * 100);

const BENEFITS = [
  {
    icon: 'gift' as const,
    title: `${COMMISSION_PCT}% recorrente`,
    description: 'Comissão sobre assinaturas indicadas por até 12 meses.',
  },
  {
    icon: 'link' as const,
    title: 'Link exclusivo',
    description: 'Compartilhe seu código e atribua clientes automaticamente.',
  },
  {
    icon: 'wallet' as const,
    title: 'Payout via Stripe',
    description: 'Receba transferências mensais após o período de hold.',
  },
] as const;

export function PartnerApplyPage() {
  const {
    partner,
    isLoading,
    companyName,
    cnpj,
    fieldErrors,
    submitting,
    onCompanyNameChange,
    onCnpjChange,
    submit,
  } = usePartnerApply();

  if (isLoading) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center px-s4"
        role="status"
        aria-live="polite"
      >
        <Spinner />
        <span className="sr-only">Carregando formulário de parceiro</span>
      </div>
    );
  }

  if (partner) return null;

  return (
    <main className="mx-auto flex w-full max-w-lg flex-col gap-s6 px-s4 py-s4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-s6 sm:py-s6 md:max-w-2xl md:px-s8 md:py-s8">
      <PageHeader
        title="Seja parceiro Adlitics"
        subtitle="Indique clientes e ganhe comissão recorrente sobre assinaturas"
        titleClassName="text-xl font-semibold tracking-tight text-default sm:text-2xl"
        subtitleClassName="text-sm text-muted sm:text-[15px]"
        hideBackButton
      />

      <section
        className="flex flex-col gap-s3"
        aria-labelledby="partner-benefits-title"
      >
        <h2 id="partner-benefits-title" className="sr-only">
          Benefícios do programa
        </h2>
        <ul className="grid list-none grid-cols-1 gap-s3 sm:grid-cols-3">
          {BENEFITS.map((item) => (
            <li
              key={item.title}
              className="flex flex-row items-start gap-s3 rounded-lg border border-subtle bg-card p-s4 sm:flex-col sm:gap-s2"
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-md bg-[var(--brand-50)] text-[var(--brand-600)]"
                aria-hidden
              >
                <AppIcon name={item.icon} className="size-[18px]" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-default">{item.title}</p>
                <p className="mt-s1 text-sm text-muted">{item.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <article
        className="rounded-xl border border-subtle bg-card p-s5 shadow-xs sm:p-s6"
        aria-labelledby="partner-apply-form-title"
      >
        <header className="mb-s5 flex flex-col gap-s1">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
            Candidatura
          </p>
          <h2 id="partner-apply-form-title" className="text-lg font-semibold text-default">
            Dados da agência
          </h2>
          <p className="text-sm text-muted">
            Envie sua candidatura. Após aprovação, você acessa o portal e o link de indicação.
          </p>
        </header>

        <form onSubmit={submit} className="flex flex-col gap-s4" noValidate>
          <fieldset className="flex flex-col gap-s4 border-0 p-0">
            <legend className="sr-only">Informações da empresa</legend>

            <Input
              label="Nome da agência"
              id="partner-company-name"
              name="companyName"
              value={companyName}
              onChange={(e) => onCompanyNameChange(e.target.value)}
              autoComplete="organization"
              required
              error={fieldErrors.companyName}
              inputSize="lg"
            />

            <Input
              label="CNPJ"
              id="partner-cnpj"
              name="cnpj"
              value={cnpj}
              onChange={(e) => onCnpjChange(e.target.value)}
              inputMode="numeric"
              autoComplete="off"
              helperText="Opcional — formato 00.000.000/0000-00"
              error={fieldErrors.cnpj}
              inputSize="lg"
              className="font-mono tabular-nums"
            />
          </fieldset>

          <Button
            type="submit"
            size="lg"
            className="mt-s2 min-h-11 w-full touch-manipulation"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? 'Enviando…' : 'Enviar candidatura'}
          </Button>

          <p className="text-center text-sm text-muted">
            Ao enviar, sua conta fica como candidata até a análise da equipe Adlitics.
          </p>
        </form>
      </article>
    </main>
  );
}
